// packages/web-ui/src/components/file-tree/FileContextMenu.tsx
//
// Story W3-S5 AC #4 — right-click context menu for the file tree.
//
// This is a portal-free, click-outside-aware menu rendered at fixed
// `(x, y)` coordinates. Items per epic AC #4:
//   - Open
//   - Open in syside IDE   (files only; disabled when capability missing)
//   - Reveal in Finder
//   - Copy path
//   - Rename               (files only)
//
// Per AC #4.2, dir rows hide `Open in syside IDE` and `Rename`.
// Per AC #4.3, the syside-IDE item is `aria-disabled="true"` when the
// `sysideAvailable` prop is `false`; the click handler still fires (so the
// W3-S3 hook can surface its `CAPABILITY_MISSING` toast) but is a no-op here.
//
// A11y: keyboard nav (ArrowUp/ArrowDown move focus, Enter activates, Esc closes).
// Focus is trapped inside the menu while open; closing returns focus to the
// row that triggered it (consumer concern).

import type { JSX, KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { FileTreeNode } from '../../api/clients/files.js';

export type ContextMenuAction =
  | 'open'
  | 'openInSyside'
  | 'revealInFinder'
  | 'copyPath'
  | 'rename';

export interface ContextMenuItem {
  action: ContextMenuAction;
  label: string;
  disabled?: boolean;
}

export interface FileContextMenuProps {
  /** The right-clicked node. Menu unmounts when `null`. */
  node: FileTreeNode | null;
  /** Pixel coordinates (clientX, clientY). */
  x: number;
  y: number;
  /** Whether the syside-IDE capability is available; gates the item's enabled state. */
  sysideAvailable?: boolean;
  /** Action handler — invoked with the action id. Always called; consumer routes. */
  onAction: (action: ContextMenuAction, node: FileTreeNode) => void;
  /** Close-menu callback (Esc, click outside, action selected). */
  onClose: () => void;
}

const FILE_ITEMS: readonly ContextMenuItem[] = [
  { action: 'open', label: 'Open' },
  { action: 'openInSyside', label: 'Open in syside IDE' },
  { action: 'revealInFinder', label: 'Reveal in Finder' },
  { action: 'copyPath', label: 'Copy path' },
  { action: 'rename', label: 'Rename' },
];

const DIR_ITEMS: readonly ContextMenuItem[] = [
  { action: 'open', label: 'Open' },
  { action: 'revealInFinder', label: 'Reveal in Finder' },
  { action: 'copyPath', label: 'Copy path' },
];

/**
 * Build the menu item list per AC #4.2 — dirs get a 3-item subset; the
 * syside-IDE item is marked disabled when capability is missing.
 */
export function buildMenuItems(
  node: FileTreeNode,
  sysideAvailable: boolean,
): ContextMenuItem[] {
  const base = node.kind === 'directory' ? DIR_ITEMS : FILE_ITEMS;
  return base.map((item) =>
    item.action === 'openInSyside'
      ? { ...item, disabled: !sysideAvailable }
      : item,
  );
}

export function FileContextMenu(props: FileContextMenuProps): JSX.Element | null {
  const { node, x, y, sysideAvailable, onAction, onClose } = props;
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLLIElement | null>>([]);

  useEffect(() => {
    if (node === null) return;
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target;
      if (target instanceof Node && menuRef.current?.contains(target) === true) return;
      onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [node, onClose]);

  useEffect(() => {
    if (node !== null) {
      itemRefs.current[0]?.focus();
    }
  }, [node]);

  const handleAction = useCallback(
    (action: ContextMenuAction): void => {
      if (node === null) return;
      onAction(action, node);
      onClose();
    },
    [node, onAction, onClose],
  );

  const moveFocus = useCallback((delta: 1 | -1, length: number) => {
    const cur = itemRefs.current.findIndex((el) => el === document.activeElement);
    const startIdx = cur === -1 ? 0 : cur;
    let next = startIdx + delta;
    for (let attempts = 0; attempts < length; attempts++) {
      next = (next + length) % length;
      const el = itemRefs.current[next];
      if (el !== null && el !== undefined && el.getAttribute('aria-disabled') !== 'true') {
        el.focus();
        return;
      }
      next += delta;
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, length: number) => {
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          onClose();
          return;
        case 'ArrowDown':
          event.preventDefault();
          moveFocus(+1, length);
          return;
        case 'ArrowUp':
          event.preventDefault();
          moveFocus(-1, length);
          return;
        default:
          return;
      }
    },
    [moveFocus, onClose],
  );

  if (node === null) return null;

  const items = buildMenuItems(node, sysideAvailable ?? true);
  itemRefs.current = new Array(items.length).fill(null);

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label="File actions"
      data-testid="file-context-menu"
      data-node-path={node.path}
      data-node-kind={node.kind}
      style={{ position: 'fixed', top: y, left: x, zIndex: 50 }}
      className="border-taupe-500 bg-cream-50 min-w-[180px] rounded-none border py-1"
      onKeyDown={(e) => handleKeyDown(e, items.length)}
    >
      <ul className="m-0 list-none p-0">
        {items.map((item, i) => (
          <li
            key={item.action}
            ref={(el) => {
              itemRefs.current[i] = el;
            }}
            role="menuitem"
            tabIndex={0}
            data-testid={`file-context-menu-item-${item.action}`}
            data-action={item.action}
            aria-label={item.label}
            aria-disabled={item.disabled === true ? 'true' : 'false'}
            className={
              'cursor-pointer px-3 py-1 text-xs hover:bg-cream-200 focus:bg-cream-200 outline-none '
              + (item.disabled === true ? 'opacity-50' : '')
            }
            onClick={() => {
              if (item.disabled === true) return;
              handleAction(item.action);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (item.disabled === true) return;
                handleAction(item.action);
              }
            }}
          >
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
